import React, { FC } from 'react';
import intl from 'react-intl-universal';
import {
  Typography,
  FormItem,
  TextField,
  TextArea,
  Dropdown,
  Tooltip,
  Icon,
  UsersPicker,
  Button,
} from '@getsynapse/design-system';
import type { LearningTeam } from 'types/store/learningTeam';
import { Option, UserOption } from 'utils/customTypes';
import { SHORT_INPUTS_LENGTH } from 'utils/constants';
import { useBasicInfoForm } from '../../hooks/useBasicInfoForm';
import UnsavedChangesPrompt from 'Organisms/UnsavedChangesPrompt/UnsavedChangesPrompt';

interface BasicInformationFormProps {
  team: LearningTeam;
}

const BasicInformationForm: FC<BasicInformationFormProps> = () => {
  const {
    basicInfo,
    learningTeamsOptions,
    selectedParentTeam,
    teamManagersOptions,
    selectedTeamManagers,
    handleChangeField,
    canSave,
    handleUpdate,
    handleCancel,
    isUpdatingLearningTeam,
    hasChanges,
  } = useBasicInfoForm();
  return (
    <div className='grid grid-cols-1 gap-x-10 gap-y-4 md:grid-cols-2 2xl:gap-x-20'>
      {/* Title - full width */}
      <div className='col-span-1 mb-4 md:col-span-2'>
        <Typography variant='h5' className='text-neutral-black'>
          {intl.get('TITLES.BASIC_INFORMATION_TITLE')}
        </Typography>
      </div>

      {/* Left column - stacked fields */}
      <div className='flex flex-col gap-y-4'>
        <FormItem>
          <TextField
            label={intl.get('TEAM_NAME', { num: 1 })}
            labelProps={{
              required: true,
            }}
            variant='text'
            length='medium'
            data-cy='team-name_input'
            placeholder={intl.get('ADD_TEAM_MODAL.TEAM_NAME_PLACEHOLDER')}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              handleChangeField('name', event.target.value);
            }}
            value={basicInfo?.name ?? ''}
            displayCharactersCount
            maxLength={SHORT_INPUTS_LENGTH}
          />
        </FormItem>
        <FormItem
          label={intl.get(
            'ORG_SETTINGS_PAGE.LEARNING_TEAMS.ADD_TEAM_MODAL.REPORTS_TO'
          )}
        >
          <Dropdown
            options={learningTeamsOptions}
            values={selectedParentTeam ? [selectedParentTeam] : []}
            triggerProps={{
              className: 'h-6',
              placeholder: intl.get(
                'ORG_SETTINGS_PAGE.LEARNING_TEAMS.ADD_TEAM_MODAL.SELECT_TEAM'
              ),
            }}
            onChange={(option: Option) => {
              handleChangeField('parent_id', option.value);
            }}
          />
        </FormItem>
        <FormItem
          label={
            <div className='flex items-center'>
              <span className='mr-1'>{intl.get('ENTITIES.TEAM_MANAGER')}</span>
              <Tooltip
                trigger={
                  <div className='flex items-center'>
                    <Icon
                      name='information-circle'
                      className='text-lg text-primary-dark'
                    />
                  </div>
                }
                ariaId='review-allocation-warning'
                openMode='hover2'
                timeout={10}
                position='topRight'
                showPopper
                usePortal
              >
                <Typography className='text-neutral-white' variant='body2'>
                  {intl.get(
                    'PEOPLE.RESOURCE_ALLOCATION.TABLE.TEAM_MANAGER_ASSIGNMENT'
                  )}
                </Typography>
              </Tooltip>
            </div>
          }
        >
          <UsersPicker
            deactivatedUserText={intl.get('DEACTIVATED')}
            usersList={teamManagersOptions}
            selectedUsersList={selectedTeamManagers}
            onChange={(users: UserOption[]) =>
              handleChangeField(
                'teamManagers',
                users.map((user) => user.value)
              )
            }
            triggerText={intl.get(
              'ORG_SETTINGS_PAGE.LEARNING_TEAMS.ADD_TEAM_MODAL.ADD_MANAGER'
            )}
            triggerProps={{ 'data-cy': 'add-managers_trigger' }}
          />
        </FormItem>
      </div>

      {/* Right column - Description fills height */}
      <FormItem className='flex flex-col'>
        <TextArea
          className='flex flex-col flex-grow'
          label={intl.get('DESCRIPTION', { num: 1 })}
          textAreaProps={{
            className: 'h-30',
            placeholder: intl.get('ADD_TEAM_MODAL.DESCRIPTION_PLACEHOLDER'),
          }}
          value={basicInfo?.description ?? ''}
          onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
            handleChangeField('description', event.target.value);
          }}
        />
      </FormItem>
      <div className='col-span-1 flex justify-start gap-x-4 mt-4 md:col-span-2'>
        <Button
          variant='primary'
          onClick={handleUpdate}
          disabled={!canSave}
          loading={isUpdatingLearningTeam}
        >
          {intl.get('SAVE')}
        </Button>
        <Button
          variant='tertiary'
          onClick={handleCancel}
          disabled={!hasChanges}
        >
          {intl.get('CANCEL')}
        </Button>
      </div>
      <UnsavedChangesPrompt hasChanges={hasChanges} />
    </div>
  );
};

export default BasicInformationForm;
